import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../components/global/Button';
import RegisterPhotoManagerButton from './RegisterPhotoManagerButton';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { sanitizePromoCode } from '../../../../components/global/PromoCodeInput/helpers';
import { getFormikTouchedErrors } from '../../../../util/formik';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import PromoCodeInput from '../../../../components/global/PromoCodeInput/PromoCodeInput';

const propTypes = {
  photoManagerSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  values: PropTypes.shape({
    plan_id: PropTypes.string,
  }),
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    promo_code: PropTypes.string,
  }),
  touched: PropTypes.shape({
    promo_code: PropTypes.bool,
  }),
  accountPromoCode: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
  }),
};

const RegisterPhotoManagerForm = ({
  values,
  handleChange,
  photoManagerSubscriptionItems,
  errors,
  touched,
  handleSubmit,
}) => {
  const [accountPromoCode, setAccountPromoCode] = useState(null);
  const checkPromoCodeRef = useRef();
  const [shouldCheckPromoCode, setShouldCheckPromoCode] = useState(false);

  if (!photoManagerSubscriptionItems) {
    return null;
  }

  const monthlyPlan = photoManagerSubscriptionItems.find(
    item =>
      item.billing_period === PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
  );
  const yearlyPlan = photoManagerSubscriptionItems.find(
    item =>
      item.billing_period === PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
  );

  const errorList = Object.values(
    getFormikTouchedErrors(errors, touched) ?? {},
  );

  return (
    <form
      onSubmit={async e => {
        e.preventDefault();
        if (shouldCheckPromoCode && checkPromoCodeRef.current) {
          const success = await checkPromoCodeRef.current();
          if (!success) {
            return;
          }
        }
        handleSubmit();
      }}
    >
      <div className="register-view-photo-manager-form">
        <div className="row">
          {(!values.promo_code ||
            !accountPromoCode ||
            [ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY].includes(
              accountPromoCode.type,
            )) && (
            <RegisterPhotoManagerButton
              onClick={photoManagerPlan => {
                handleChange({
                  target: {
                    name: 'plan_id',
                    value: photoManagerPlan.id,
                  },
                });
              }}
              photoManagerSubscriptionItem={monthlyPlan}
              selected={values.plan_id === monthlyPlan.id}
              wrapperClassName={
                accountPromoCode && values.promo_code ? 'col-12' : 'col-6'
              }
            />
          )}
          {(!values.promo_code ||
            !accountPromoCode ||
            [
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
            ].includes(accountPromoCode.type)) && (
            <RegisterPhotoManagerButton
              onClick={photoManagerPlan => {
                handleChange({
                  target: {
                    name: 'plan_id',
                    value: photoManagerPlan.id,
                  },
                });
              }}
              photoManagerSubscriptionItem={yearlyPlan}
              selected={values.plan_id === yearlyPlan.id}
              wrapperClassName={
                accountPromoCode && values.promo_code ? 'col-12' : 'col-6'
              }
              note="Save 17%!"
            />
          )}
          <div className="col-12">
            <PromoCodeInput
              accountReferralTypes={[
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
              ]}
              className="view-register-promo-box mt-2"
              labelSrOnly
              placeholder="Have a Promo Code?"
              fetchPolicy="cache-first"
              onPromoCodeChange={code =>
                handleChange({
                  target: {
                    name: 'promo_code',
                    value: sanitizePromoCode(code),
                  },
                })
              }
              onDismiss={() => {
                handleChange({
                  target: {
                    name: 'promo_code',
                    value: '',
                  },
                });

                setAccountPromoCode(null);
              }}
              onDefaultPromoCodeParsed={() => {
                handleChange({
                  target: {
                    name: 'default_promo_code',
                    value: '',
                  },
                });
              }}
              onPromoCodeVerified={newAccountPromoCode => {
                if (newAccountPromoCode) {
                  handleChange({
                    target: {
                      name: 'plan_id',
                      value:
                        newAccountPromoCode.type ===
                        ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY
                          ? photoManagerSubscriptionItems.find(
                              item =>
                                item.billing_period ===
                                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
                            )?.id
                          : photoManagerSubscriptionItems.find(
                              item =>
                                item.billing_period ===
                                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
                            )?.id,
                    },
                  });

                  setAccountPromoCode(newAccountPromoCode);
                }
              }}
              verifyBillingPeriod={false}
              defaultPromoCode={values.promo_code || values.default_promo_code}
              onPromoCodeUpdate={(newPromoCode, isApplied, checkPromoCode) => {
                if (newPromoCode && !isApplied) {
                  setShouldCheckPromoCode(true);
                } else {
                  setShouldCheckPromoCode(false);
                }
                checkPromoCodeRef.current = checkPromoCode;
              }}
            />
          </div>
        </div>

        {errorList?.length > 0 && (
          <ul className="text-danger">
            {errorList.map(error => (
              <li key={error} className="text-danger">
                {error}
              </li>
            ))}
          </ul>
        )}

        {values.plan_id && (
          <div className="mt-3 text-center">
            <Button type="submit">Continue</Button>
          </div>
        )}
      </div>
    </form>
  );
};

RegisterPhotoManagerForm.propTypes = propTypes;

export default RegisterPhotoManagerForm;
